import { Property } from '@square/ignition';

type ApiExplorerFieldType =
  | 'primitive'
  | 'enum'
  | 'object'
  | 'array'
  | 'schema'
  | 'any'
  | 'map';

const getFieldTypeForProperty = (property: Property): ApiExplorerFieldType => {
  if (property.isEnum && property.entity) {
    return 'enum';
  } else if (property.isAny) {
    return 'any';
  } else if ((property.isEntity || property.isRequest) && property.entity) {
    return 'object';
  } else if (property.isCustomAttributeDefinitionSchema) {
    return 'schema';
  } else if (property.isArray) {
    return 'array';
  } else if (property.isMap) {
    return 'map';
  } else {
    return 'primitive';
  }
};

const getFieldTypeForItemProperty = (
  property: Property
): ApiExplorerFieldType => {
  if (property.containsEnum && property.itemEntity) {
    return 'enum';
  } else if (property.containsEntity && property.itemEntity) {
    return 'object';
  } else {
    return 'primitive';
  }
};

type ApiExplorerDatetimeFormat = 'datetime' | 'duration';

const datetimeRegex = /rfc 3339/i;
const timestampRegex = /timestamp/i;
const durationRegex = /duration/i;

/**
 * Determines the datetime format of the property, if available.
 * If writers add RFC 3339 in the description, we can assume it's a datetime.
 * If, additionally, they add `duration`, then it is an RFC 3339 duration.
 * This code is taken from ignition so that we can add more information to the fields
 * @param property
 * @returns datetime if it's a datetime format, duration if a duration format, or undefined if none
 */
const getDateTimeFormat = (
  property: Property
): ApiExplorerDatetimeFormat | undefined => {
  if (!property.description) {
    return undefined;
  }

  // This is actually how ignition determines the datetime. The only metadata
  // we have is when writers add these to the description.
  const isDatetime = datetimeRegex.test(property.description);
  const isTimestamp = timestampRegex.test(property.description);
  const isDuration = durationRegex.test(property.description);

  if (isDatetime) {
    // Only mark it as a duration if it's not a timestamp.
    return !isTimestamp && isDuration ? 'duration' : 'datetime';
  }

  return undefined;
};

export {
  type ApiExplorerFieldType,
  type ApiExplorerDatetimeFormat,
  getFieldTypeForProperty,
  getFieldTypeForItemProperty,
  getDateTimeFormat,
};
